"use strict";

const walletRenderer = require("./walletRenderer");
const gating = require("./walletGating");
const expressDom = require("./expressDom");
const diagnostics = require("./expressDiagnostics");
const preflight = require("./expressPreflight");
const confirmationForm = require("./expressConfirmationForm");
const buttonOptions = require("./walletButtonOptions");

/**
 * Which wallets this particular container may mount.
 *
 * DGF-4614. Until now every container on a surface mounted the whole `cartExpressMethods` list, which
 * was right while one page had one container per surface. The mobile sticky bar breaks that: it is a
 * second cart container on the same page and must carry Apple Pay ALONE — partly because that is the
 * two-button layout that was asked for, and partly because Google Pay's SDK button renders with a
 * fixed internal id (`#gpay-button-online-api-id`), so a second Google Pay mount on the same document
 * would duplicate it.
 *
 * Always an INTERSECTION with what the server offered, never a replacement for it. An allow-list can
 * only narrow the list, so a container cannot name a wallet that `/paymentMethods` did not return for
 * the shopper's territory, and no markup change can widen what is on offer. An absent or empty
 * attribute means "no restriction", which is what every pre-existing container has and why none of
 * them change behaviour.
 *
 * DGF-4617 widens "what the server offered" by one source, without weakening the invariant above:
 * LOCAL wallets (direct PayPal) are appended from `data-express-local-wallets`, an attribute the
 * SERVER emits only when that wallet's own site preference is on — so it is still server-gated, it
 * is just gated by a Superdry preference instead of Adyen's /paymentMethods response. The attribute
 * is filtered to gating.LOCAL_WALLETS, so markup alone still cannot conjure an Adyen wallet, and
 * the allow-list intersects the COMBINED list, so it keeps its narrow-only meaning for both kinds.
 *
 * DGF-4659 — the merchant's ExpressPayments_order preference now covers local wallets too, sorted
 * in HERE rather than server-side. getCheckoutExpressPaymentMethods.js:17-25 sorts cartExpressMethods
 * against that same preference, but it only ever sees Adyen's own wallets — the local wallet is
 * appended above, by this client, after that sort already ran, so the server literally cannot place
 * it: it does not know local wallets exist. Sorting client-side, on the full combined list, is the
 * only place the preference can be honoured for every wallet at once. See sortByExpressOrder.
 *
 * @param {Object} container - the express container element
 * @param {Array} cartExpressMethods - the wallets the server offered for this basket
 * @returns {Array} the wallets this container may mount, ordered by ExpressPayments_order when the
 *          container carries one, otherwise in the server's order (today's behaviour, unchanged)
 */
function resolveWalletTypes(container, cartExpressMethods) {
    const offered = cartExpressMethods || [];
    const localWallets = (container.getAttribute(LOCAL_WALLETS_ATTRIBUTE) || "")
        .split(",")
        .map((wallet) => wallet.trim())
        .filter((wallet) => wallet && gating.isLocalWallet(wallet) && offered.indexOf(wallet) === -1);
    const combined = sortByExpressOrder(offered.concat(localWallets), container);

    const allowList = (container.getAttribute(WALLET_ALLOW_LIST_ATTRIBUTE) || "")
        .split(",")
        .map((wallet) => wallet.trim())
        .filter((wallet) => wallet);

    if (!allowList.length) {
        return combined;
    }

    return combined.filter((wallet) => allowList.indexOf(wallet) > -1);
}

/**
 * DGF-4659 — orders the combined (Adyen + local) wallet list by `data-express-order`, the
 * ExpressPayments_order preference the server resolved once into that attribute.
 *
 * An absent or blank attribute returns `wallets` untouched — no surface that does not emit the
 * attribute changes behaviour, which is every surface except the ones DGF-4659 touched.
 *
 * A wallet the preference does not name sorts LAST rather than first. `Array.prototype.indexOf`
 * returns -1 for "not found", and sorting directly on that would put every unnamed wallet ahead of
 * every named one — the opposite of "unknown wallets are lowest priority". Each unmatched wallet is
 * given a rank of `order.length` instead, which is greater than every real match's rank.
 *
 * The sort is a decorate-sort-undecorate rather than a bare `Array.prototype.sort` comparator, so
 * that two wallets tied on rank (both unmatched, or — impossible today, but not assumed away —
 * both matching the same token) are explicitly ordered by their ORIGINAL position rather than by
 * relying on engine-specific sort stability.
 *
 * @param {string[]} wallets - the combined wallet list, in today's (pre-sort) order
 * @param {Object} container - the express container element
 * @returns {string[]} `wallets`, reordered by the preference; the same array contents otherwise
 */
function sortByExpressOrder(wallets, container) {
    const orderAttribute = (container.getAttribute(ORDER_ATTRIBUTE) || "").trim();
    if (!orderAttribute) {
        return wallets;
    }
    const order = orderAttribute.split(",").map((wallet) => wallet.trim());

    return wallets
        .map((wallet, position) => ({ wallet: wallet, position: position }))
        .sort((a, b) => {
            const rankA = order.indexOf(a.wallet);
            const rankB = order.indexOf(b.wallet);
            const orderedRankA = rankA === -1 ? order.length : rankA;
            const orderedRankB = rankB === -1 ? order.length : rankB;
            return orderedRankA !== orderedRankB
                ? orderedRankA - orderedRankB
                : a.position - b.position;
        })
        .map((entry) => entry.wallet);
}

/**
 * DGF-3054 — express wallets on the bag surfaces: the cart page and the header mini bag.
 *
 * Replaces app_adyen_SFRA/.../adyen/express/cart/expressPayments.js as the orchestrator (that
 * cartridge cannot be modified) while reusing its ApplePay and GooglePay classes unchanged, so
 * the payment flow itself stays vendor code.
 *
 * Every dependency is injected. The entry point js/cartExpressPayments.js supplies them through
 * the webpack `adyenSFRA` alias; tests supply fakes, which is why nothing here requires an alias
 * or touches a global.
 */

/** Marks a container as handled so a repeated init cannot mount twice into it. */
const IN_PROGRESS_ATTRIBUTE = "data-express-rendering";
// DGF-4614 — optional per-container restriction on which wallets may mount there.
const WALLET_ALLOW_LIST_ATTRIBUTE = "data-express-wallets";
// DGF-4617 — server-gated list of local (non-Adyen) wallets this container offers.
const LOCAL_WALLETS_ATTRIBUTE = "data-express-local-wallets";
// DGF-4659 — ExpressPayments_order, resolved server-side once and handed to the client so the
// combined (Adyen + local) list can be sorted by it. See sortByExpressOrder.
const ORDER_ATTRIBUTE = "data-express-order";

/**
 * Guards event binding across bundle instances, not just across calls. The mini bag fragment
 * carries the same script tag as the cart page and jQuery re-evaluates it on every popover
 * refresh, which produces a fresh module instance each time — a module-level flag alone would let
 * the cart:update handler be bound repeatedly.
 */
const HANDLERS_BOUND_FLAG = "sdExpressCartHandlersBound";

/**
 * Key for the shared refresh queue. PR #1979 finding 2.
 *
 * On window for the same reason HANDLERS_BOUND_FLAG is, and it is not optional here. Bag refreshes
 * come from three places that overlap freely — init() on first paint, the cart:update handler, and
 * the mini bag fragment re-evaluating this bundle — and the third produces a whole new module
 * instance with its own module scope (cart/expressPayments.isml:95 puts the script tag inside the
 * fragment jQuery injects). A module-level queue would serialise only the refreshes issued by one
 * instance while the other instance's response stayed free to publish window.basketAmount
 * underneath it, which is half the defect still shipped.
 */
const REFRESH_STATE_FLAG = "sdExpressCartRefreshState";

let cachedDeps = null;

/**
 * Where the refresh state lives when no window was injected — unit tests calling refresh() with a
 * bare deps object. Never reached in the browser, where deps.win is window.
 */
const detachedRefreshState = {};

/**
 * Builds the Adyen SDK component for one wallet.
 *
 * The vendor classes take (configuration, applicationInfo, adyenTranslations, isExpressPdp,
 * initialAmount). On the bag surfaces initialAmount is omitted deliberately: the classes then read
 * window.basketAmount, which refresh() keeps in step with the server's view of the basket.
 *
 * @param {Object} deps - injected dependencies
 * @param {Object} paymentMethodsResponse - response from Adyen-GetExpressPaymentMethods
 * @param {string} surface - "cart" or "minicart"
 * @returns {Function} createComponent implementation for walletRenderer
 */
function buildComponentFactory(deps, paymentMethodsResponse, surface) {
    return async function createComponent(walletType, walletConfiguration, element) {
        // Local wallets (DGF-4617 direct PayPal) own their whole flow — SDK, order creation,
        // authorisation — so none of the Adyen construction below applies to them.
        //
        // Routed on the CONTAINER's data-express-local-wallets, not on the wallet name: "paypal" is
        // both an Adyen method and our direct integration, so the name alone cannot say which one
        // the server meant. See walletGating.isLocalWalletOnContainer.
        if (gating.isLocalWalletOnContainer(element && element.parentNode, walletType)) {
            const localWallet = deps.localWallets && deps.localWallets[walletType];
            if (!localWallet) {
                return null;
            }
            return localWallet.createComponent({
                deps: deps,
                surface: surface,
                container: element && element.parentNode
            });
        }

        // A lookup rather than the old `applepay ? ApplePay : GooglePay` ternary, whose fallthrough
        // constructed a Google Pay instance for ANY unrecognised type.
        const walletClasses = deps.walletClasses
            || { applepay: deps.ApplePay, googlepay: deps.GooglePay };
        const WalletClass = walletClasses[walletType];
        if (!WalletClass) {
            return null;
        }
        const wallet = new WalletClass(
            walletConfiguration,
            paymentMethodsResponse.applicationInfo,
            paymentMethodsResponse.adyenTranslations,
            false
        );
        applyButtonStyling(wallet, walletType);
        preflight.wrapWallet(wallet, deps, surface);
        return wallet.getComponent();
    };
}

/**
 * Applies Superdry's wallet button options.
 *
 * Delegates to walletButtonOptions, which both the bag and the PDP now share — the PDP applied none
 * of this before, which is what made its buttons narrower than add-to-bag and unequal to each other.
 * Kept as an export here because it is part of this module's tested surface.
 *
 * @param {Object} wallet - vendor wallet instance
 * @param {string} walletType - "applepay" or "googlepay"
 * @returns {Object} the same wallet instance
 */
function applyButtonStyling(wallet, walletType) {
    return buttonOptions.applyButtonStyling(wallet, walletType);
}

/**
 * Renders every express container currently in the document.
 *
 * @param {Object} deps - injected dependencies
 * @param {Object} paymentMethodsResponse - response from Adyen-GetExpressPaymentMethods
 * @param {Object} [options] - { surface, force }
 * @returns {Promise<Object[]>} per-container render results
 */
async function renderContainers(deps, paymentMethodsResponse, options) {
    const settings = options || {};
    const containers = expressDom.findExpressContainers(deps.doc, settings.surface)
        .filter((container) => container.getAttribute("data-express-surface") !== "pdp");
    const results = [];

    for (let i = 0; i < containers.length; i += 1) {
        const container = containers[i];
        if (container.getAttribute(IN_PROGRESS_ATTRIBUTE) === "true") {
            // Only reachable from a second module instance that has no window to share the refresh
            // queue with (see REFRESH_STATE_FLAG); within one page the queue means no two renders of
            // ours overlap. It is kept because this is the last line of defence against two passes
            // being inside walletRenderer's clear-then-mount at once, but it must never again be the
            // thing that decides a response is dropped — that WAS the defect: a newer response
            // arriving while an older render owned the container was silently thrown away, and the
            // sheet kept the pre-mutation total until some other event happened to fire.
            diagnostics.record(deps, {
                surface: container.getAttribute("data-express-surface") || "cart",
                wallet: "*",
                reason: "render-already-in-progress",
                detail: "another instance owns this container; this pass left it alone"
            });
            continue;
        }
        const alreadyMounted = container.getAttribute(walletRenderer.MOUNTED_ATTRIBUTE) === "true";
        if (alreadyMounted && !settings.force) {
            // A second init (mini bag fragment re-evaluating the bundle) must not re-mount a
            // surface that is already showing its wallets.
            continue;
        }
        container.setAttribute(IN_PROGRESS_ATTRIBUTE, "true");
        expressDom.setSurfaceError(container, "");
        const surface = container.getAttribute("data-express-surface") || "cart";
        const walletTypes = resolveWalletTypes(
            container,
            paymentMethodsResponse.cartExpressMethods
        );
        try {
            // eslint-disable-next-line no-await-in-loop
            const result = await walletRenderer.renderWallets({
                doc: deps.doc,
                container: container,
                surface: surface,
                walletTypes: walletTypes,
                adyenPaymentMethods: paymentMethodsResponse.AdyenPaymentMethods,
                createComponent: buildComponentFactory(deps, paymentMethodsResponse, surface),
                nav: deps.nav
            });
            expressDom.setSurfaceVisibility(container, result.mounted.length > 0);
            // Reported against this container's own list, not the server's: on the sticky bar the
            // difference is Google Pay, and calling it "skipped" there would be a diagnostic for a
            // wallet the container was never asked to mount.
            diagnostics.recordRenderResult(
                deps,
                surface,
                result,
                walletTypes
            );
            results.push(result);
        } finally {
            container.removeAttribute(IN_PROGRESS_ATTRIBUTE);
        }
    }

    return results;
}

/**
 * Fetches the express payment methods.
 *
 * Publishing the amount used to happen here, the moment the response arrived, which is what allowed
 * an older response to overwrite a newer one — see publishBasketAmount and runRefresh.
 *
 * @param {Object} deps - injected dependencies
 * @returns {Promise<Object|null>} the payment methods response
 */
async function fetchPaymentMethods(deps) {
    // When Adyen express is off but a local wallet (direct PayPal) is on, there is nothing to ask
    // Adyen for — the express metadata (and with it the Adyen endpoints) is not even on the page.
    // An empty response keeps the whole render pipeline unchanged: the Adyen offer list is empty,
    // and local wallets join via the container's data-express-local-wallets attribute.
    if (!isAdyenExpressEnabled(deps) || typeof deps.getExpressPaymentMethods !== "function") {
        return { cartExpressMethods: [], AdyenPaymentMethods: { paymentMethods: [] } };
    }
    const response = await deps.getExpressPaymentMethods();
    return response || null;
}

/**
 * @param {Object} deps - injected dependencies
 * @returns {boolean} whether Adyen express is enabled for the bag surfaces
 */
function isAdyenExpressEnabled(deps) {
    return Boolean(deps.win && deps.win.areCartExpressPaymentsEnabled === "true");
}

/**
 * @param {Object} doc - document-like object
 * @returns {boolean} whether any express container on the page offers a local wallet
 */
function hasLocalWalletContainers(doc) {
    return expressDom.findExpressContainers(doc).some(
        (container) => Boolean(container.getAttribute(LOCAL_WALLETS_ATTRIBUTE))
    );
}

/**
 * Publishes the basket total the wallets will charge as window.basketAmount.
 *
 * The wallets read window.basketAmount when they are constructed, and the cart page mutates the
 * basket without reloading (quantity, removal, promo code, customer credit), so a stale value
 * would put the wrong total in the payment sheet. The response's `amount` is computed server-side
 * from the current basket in minor units (getCheckoutExpressPaymentMethods.js:32-35, :91), so it
 * is used as the source of truth rather than anything parsed out of the DOM.
 *
 * Callers must have established that this response is the newest one first: window.basketAmount is a
 * single global read by every wallet on the page, so whoever writes last wins regardless of which
 * basket state they describe.
 *
 * @param {Object} deps - injected dependencies
 * @param {Object} response - the payment methods response
 * @returns {void}
 */
function publishBasketAmount(deps, response) {
    if (!response || !response.amount || !response.amount.currency || !deps.win) {
        return;
    }
    deps.win.basketAmount = JSON.stringify({
        currency: response.amount.currency,
        value: response.amount.value
    });
}

/**
 * The shared refresh queue for this page.
 *
 * @param {Object} deps - injected dependencies
 * @returns {Object} { generation, queued, draining }
 */
function getRefreshState(deps) {
    const host = (deps && deps.win) || detachedRefreshState;
    if (!host[REFRESH_STATE_FLAG]) {
        host[REFRESH_STATE_FLAG] = { generation: 0, queued: null, draining: null };
    }
    return host[REFRESH_STATE_FLAG];
}

/**
 * @param {number} generation - the generation the running refresh was queued with
 * @param {Object} state - refresh state
 * @returns {boolean} whether a later refresh has been queued since
 */
function isSupersededRefresh(generation, state) {
    return generation !== state.generation;
}

/**
 * Folds a newly requested refresh into one that is already waiting.
 *
 * force is ORed rather than replaced: a cart:update collapsing into a queued init(force:false) must
 * still re-mount, because the amount it would be re-mounting for is exactly what changed. Dropping
 * the flag here would leave the wallets — and so the payment sheet — on the pre-mutation total,
 * which is the symptom this whole change exists to remove.
 *
 * The surface widens to "every surface" whenever two requests disagree, because a coalesced refresh
 * has to cover at least what each of them asked for; undefined is what findExpressContainers reads
 * as "all".
 *
 * @param {Object} existing - settings already queued
 * @param {Object} incoming - settings just requested
 * @returns {Object} merged settings
 */
function mergeRefreshSettings(existing, incoming) {
    return {
        surface: existing.surface === incoming.surface ? existing.surface : undefined,
        force: Boolean(existing.force || incoming.force)
    };
}

/**
 * One refresh: fetch, then publish and render only if this is still the newest request.
 *
 * @param {Object} deps - injected dependencies
 * @param {Object} settings - { surface, force }
 * @param {number} generation - the generation this refresh was queued with
 * @param {Object} state - refresh state
 * @returns {Promise<Object[]>} per-container render results
 */
async function runRefresh(deps, settings, generation, state) {
    if (!expressDom.findExpressContainers(deps.doc, settings.surface).length) {
        return [];
    }
    if (!expressDom.promoteTokenElement(deps.doc)) {
        // Without a token every express POST would be rejected, so nothing is offered.
        return [];
    }
    try {
        const response = await fetchPaymentMethods(deps);
        if (!response) {
            return [];
        }
        if (isSupersededRefresh(generation, state)) {
            // A newer refresh was requested while this request was in flight, and it will run the
            // moment this one returns. So this response neither publishes its amount nor mounts
            // anything: it describes a basket state the shopper has already moved on from, and both
            // of those writes are how the stale total reached the payment sheet.
            //
            // Returning early here is also what keeps a burst — cart:update and
            // cart:updateCartTotals both land on the same handler (:408) — down to one visible
            // re-mount instead of one per event.
            diagnostics.record(deps, {
                surface: settings.surface || "*",
                wallet: "*",
                reason: "superseded-refresh",
                detail: `generation ${generation} of ${state.generation} discarded before publishing`
            });
            return [];
        }
        publishBasketAmount(deps, response);
        return renderContainers(deps, response, settings);
    } catch (error) {
        expressDom.findExpressContainers(deps.doc, settings.surface).forEach((container) => {
            expressDom.setSurfaceError(container, deps.errorMessage || "");
            expressDom.setSurfaceVisibility(container, false);
        });
        return [];
    }
}

/**
 * Runs queued refreshes one at a time until none is left.
 *
 * @param {Object} state - refresh state
 * @returns {Promise<void>} resolves when the queue is empty
 */
async function drainRefreshQueue(state) {
    try {
        while (state.queued) {
            const job = state.queued;
            // Cleared BEFORE the run, not after: a refresh requested while this one is running has
            // to land in a NEW slot and run afterwards. Clearing it afterwards would fold the newer
            // request into the run already in progress and lose it — the same class of mistake as
            // the in-progress skip in renderContainers.
            state.queued = null;
            let results = [];
            try {
                // eslint-disable-next-line no-await-in-loop
                results = await runRefresh(job.deps, job.settings, job.generation, state);
            } catch (error) {
                // runRefresh already puts the failure on the surfaces. The loop has to survive it or
                // one failed request would wedge express until the next page load.
                results = [];
            }
            job.resolve(results);
        }
    } finally {
        // Nothing is awaited between the last job.resolve and here, so no refresh can slip in
        // between finding the queue empty and releasing it.
        state.draining = null;
    }
}

/**
 * Renders, or re-renders, the bag express wallets.
 *
 * Requests are queued rather than run concurrently. Every caller still gets a promise of the results
 * of the run that satisfies its request; requests that arrive while one is running are coalesced into
 * a single follow-up run, so the newest basket state is always the one that ends up published and
 * mounted, and no request is ever dropped.
 *
 * Rejected alternatives:
 *   - Leaving refreshes concurrent and only gating the publish. The wallets themselves are the other
 *     half of the problem: two runs inside walletRenderer at once clear each other's container
 *     (walletRenderer.js:71-73) while their createComponent awaits are outstanding.
 *   - Debouncing cart:update. It would delay every legitimate re-mount by the debounce interval on a
 *     payment surface to tidy up a burst, and it still would not order two responses.
 *
 * @param {Object} [deps] - injected dependencies; defaults to those captured by init
 * @param {Object} [options] - { surface, force }
 * @returns {Promise<Object[]>} per-container render results
 */
async function refresh(deps, options) {
    const dependencies = deps || cachedDeps;
    const settings = options || {};
    if (!dependencies) {
        return [];
    }

    if (!expressDom.findExpressContainers(dependencies.doc, settings.surface).length) {
        // Nothing to render into. Returned before a generation is claimed, deliberately: a refresh
        // for a surface that is not on the page — a cart:update after the mini bag popover has been
        // taken out of the DOM — must not be able to supersede a refresh that is genuinely in flight
        // and about to publish. runRefresh checks again at run time, because a queued refresh's
        // containers can appear or disappear while it waits.
        return [];
    }

    const state = getRefreshState(dependencies);
    // Claimed as the request is QUEUED, not as it starts running, so a request already in flight can
    // see that it has been superseded and decline to publish. Claiming it at run time would make the
    // check tautological — the running refresh would always be the newest.
    state.generation += 1;

    if (state.queued) {
        state.queued.deps = dependencies;
        state.queued.settings = mergeRefreshSettings(state.queued.settings, settings);
        state.queued.generation = state.generation;
        return state.queued.promise;
    }

    const job = { deps: dependencies, settings: settings, generation: state.generation };
    job.promise = new Promise(function capture(resolve) {
        job.resolve = resolve;
    });
    state.queued = job;

    if (!state.draining) {
        state.draining = drainRefreshQueue(state);
    }
    return job.promise;
}

/**
 * Binds the events that invalidate the express amount.
 *
 * cart:update covers quantity changes, line removal, promo codes and customer credit on both the
 * cart page and the mini bag, and forces a re-mount because the payment sheet total is fixed when
 * the component is constructed.
 *
 * The mini bag needs no event of its own: its markup is replaced wholesale by the
 * Cart-MiniCartShow AJAX response, which carries this bundle's script tag, and jQuery evaluates
 * appended scripts in order — so init() runs again against the freshly injected container.
 *
 * @param {Object} deps - injected dependencies
 * @returns {void}
 */
function bindEvents(deps) {
    if (!deps.$ || !deps.win || deps.win[HANDLERS_BOUND_FLAG]) {
        return;
    }
    deps.win[HANDLERS_BOUND_FLAG] = true;

    deps.$("body").on("cart:update cart:updateCartTotals", function onCartUpdate() {
        refresh(deps, { force: true });
    });
}

/**
 * Entry point.
 *
 * Idempotent: the mini bag fragment carries the same script tag as the cart page, and jQuery
 * re-evaluates it on every popover refresh, so init can run more than once per page. Containers
 * that already have wallets mounted are left alone.
 *
 * @param {Object} deps - injected dependencies
 * @returns {Promise<Object[]>} per-container render results for this pass
 */
async function init(deps) {
    cachedDeps = deps;

    // DGF-4617: local wallets (direct PayPal) are gated by their own preference, so express can be
    // live on a surface even when Adyen express is off — the server only emits
    // data-express-local-wallets when that is the case.
    if (!isAdyenExpressEnabled(deps) && !hasLocalWalletContainers(deps.doc)) {
        return [];
    }

    // Must be in place before any wallet can authorise: the vendor builds the confirmation form
    // without a CSRF token and posts it natively to a route WP1 now CSRF-validates. Only relevant
    // (and only wired) when the Adyen wallets are on the page.
    if (deps.helpers) {
        confirmationForm.patchCreateShowConfirmationForm(deps.helpers, deps);
    }

    bindEvents(deps);
    return refresh(deps, { force: false });
}

module.exports = {
    HANDLERS_BOUND_FLAG: HANDLERS_BOUND_FLAG,
    WALLET_ALLOW_LIST_ATTRIBUTE: WALLET_ALLOW_LIST_ATTRIBUTE,
    LOCAL_WALLETS_ATTRIBUTE: LOCAL_WALLETS_ATTRIBUTE,
    ORDER_ATTRIBUTE: ORDER_ATTRIBUTE,
    resolveWalletTypes: resolveWalletTypes,
    init: init,
    refresh: refresh,
    renderContainers: renderContainers,
    buildComponentFactory: buildComponentFactory,
    applyButtonStyling: applyButtonStyling,
    fetchPaymentMethods: fetchPaymentMethods,
    publishBasketAmount: publishBasketAmount,
    // exposed for tests
    _reset: function reset() {
        cachedDeps = null;
        // The per-page queue lives on the injected window, and every test builds a fresh one, so
        // only the detached fallback needs clearing here.
        delete detachedRefreshState[REFRESH_STATE_FLAG];
    }
};
